"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import {
  mediaQueries,
  sizes,
  transitionDelayTime,
  useInViewConfig,
  useTransitionUpStyles,
} from "ui"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import BlockWrapper from "../../common/BlockWrapper"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"

const StyledBlockWrapper = styled(BlockWrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${sizes.baseTriple};

  ${mediaQueries.desktopM} {
    gap: ${sizes.baseQuadric};
  }
`

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${sizes.baseQuadric};
  padding-bottom: 64px;

  ${mediaQueries.desktopM} {
    gap: ${sizes.baseQuadric};
  }
`

const StyledImageContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 214px;
  border-radius: ${sizes.borderRadiusLarge};
  overflow: hidden;

  ${mediaQueries.tablet} {
    height: 400px;
  }

  ${mediaQueries.desktop} {
    height: 679px;
  }

  ${mediaQueries.desktopXl} {
    height: 941px;
  }
`

const StyledWrapDownloadAnchor = styled.a`
  text-decoration: none;
`

interface MainImageBlockProps {
  data: any
  id?: string
}

const handleDownload = async (url: string, filename: string) => {
  try {
    // Fetch the file as a blob to force download
    const response = await fetch(url)
    const blob = await response.blob()

    // Create a blob URL
    const blobUrl = window.URL.createObjectURL(blob)

    // Create a temporary anchor element
    const link = document.createElement("a")
    link.href = blobUrl
    link.download = filename
    link.style.display = "none"

    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error("Download failed:", error)
    // Fallback: open in new tab if download fails
    window.open(url, "_blank")
  }
}

const MainImageBlock = ({ data, id }: MainImageBlockProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView, transitionDelayTime)

  // Transform raw data from CMS
  const title = data?.title?.value || ""
  const button = data?.button?.links?.[0] || ""
  const image = data?.image?.mediaItems?.[0]
  const imageSrc = image?.url ? getUmbracoMediaUrl(image.url) : ""
  const imageAlt = image?.altText || title || "Main image"

  return (
    <StyledBlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        <StyledImageContainer>
          {imageSrc && (
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              priority
              style={{ height: "100%" }}
            />
          )}
        </StyledImageContainer>

        {button && button?.name && button?.url && (
          <ButtonWithArrow
            onClick={() =>
              handleDownload(getNonUmbracoUrl(button?.url), button?.name)
            }
          >
            {button?.name}
          </ButtonWithArrow>
        )}
      </StyledContainer>
    </StyledBlockWrapper>
  )
}

export default MainImageBlock
